import { ChecklistModuleDefinition } from '../../types/due-diligence';

export const timberModule: ChecklistModuleDefinition = {
  code: 'timber',
  name: 'Timber',
  description: 'Timber inventory, value, and management',
  categories: [
    {
      code: 'timber_inventory',
      name: 'Timber Inventory',
      items: [
        {
          code: 'timber_cruise',
          title: 'Timber cruise/inventory',
          description: 'Professional timber inventory',
          isGroupTask: false,
          dataFields: [
            { key: 'cruise_done', label: 'Cruise Completed', type: 'boolean' },
            { key: 'cruise_date', label: 'Cruise Date', type: 'date' },
            { key: 'cruiser_name', label: 'Forester/Cruiser', type: 'string' },
            { key: 'timber_acres', label: 'Timber Acres', type: 'number', unit: 'acres' },
            { key: 'total_board_feet', label: 'Total Board Feet', type: 'number', unit: 'MBF' },
            { key: 'tons', label: 'Total Tons', type: 'number', unit: 'tons' },
          ],
        },
        {
          code: 'species_composition',
          title: 'Species composition',
          description: 'Tree species present',
          isGroupTask: false,
          dataFields: [
            { key: 'primary_species', label: 'Primary Species', type: 'array' },
            { key: 'secondary_species', label: 'Secondary Species', type: 'array' },
            { key: 'species_percentages', label: 'Species Breakdown', type: 'string' },
          ],
          suggestedFlags: [
            { flag: 'big_green', condition: 'High-value hardwood species' },
            { flag: 'green', condition: 'Good species mix' },
          ],
        },
        {
          code: 'timber_age_class',
          title: 'Age class distribution',
          description: 'Timber maturity assessment',
          isGroupTask: false,
          dataFields: [
            { key: 'age_classes', label: 'Age Classes Present', type: 'multiselect', options: [
              { value: 'seedling', label: 'Seedling/Sapling (0-10 yrs)' },
              { value: 'pole', label: 'Pole Timber (10-30 yrs)' },
              { value: 'small_sawtimber', label: 'Small Sawtimber (30-50 yrs)' },
              { value: 'mature_sawtimber', label: 'Mature Sawtimber (50-80 yrs)' },
              { value: 'overmature', label: 'Overmature (80+ yrs)' },
            ]},
            { key: 'distribution_notes', label: 'Distribution Notes', type: 'string' },
          ],
        },
        {
          code: 'timber_quality',
          title: 'Timber quality assessment',
          description: 'Grade and quality of standing timber',
          isGroupTask: false,
          dataFields: [
            { key: 'quality_rating', label: 'Quality Rating', type: 'select', options: [
              { value: 'excellent', label: 'Excellent - High grade' },
              { value: 'good', label: 'Good - Above average' },
              { value: 'average', label: 'Average' },
              { value: 'below_average', label: 'Below Average' },
              { value: 'poor', label: 'Poor - Pulpwood only' },
            ]},
            { key: 'grade_distribution', label: 'Grade Distribution', type: 'string' },
          ],
          suggestedFlags: [
            { flag: 'big_green', condition: 'High quality timber' },
            { flag: 'yellow', condition: 'Poor quality timber' },
          ],
        },
      ],
    },
    {
      code: 'timber_value',
      name: 'Timber Value',
      items: [
        {
          code: 'stumpage_value',
          title: 'Stumpage value estimate',
          description: 'Current value of standing timber',
          isGroupTask: false,
          dataFields: [
            { key: 'sawtimber_value', label: 'Sawtimber Value', type: 'number', unit: 'dollars' },
            { key: 'pulpwood_value', label: 'Pulpwood Value', type: 'number', unit: 'dollars' },
            { key: 'chip_n_saw_value', label: 'Chip-n-Saw Value', type: 'number', unit: 'dollars' },
            { key: 'total_stumpage', label: 'Total Stumpage Value', type: 'number', unit: 'dollars' },
            { key: 'value_per_acre', label: 'Value Per Acre', type: 'number', unit: 'dollars' },
          ],
          suggestedFlags: [
            { flag: 'big_green', condition: 'High timber value' },
          ],
        },
        {
          code: 'market_conditions',
          title: 'Current market conditions',
          description: 'Local timber market assessment',
          isGroupTask: false,
          dataFields: [
            { key: 'market_strength', label: 'Market Strength', type: 'select', options: [
              { value: 'strong', label: 'Strong' },
              { value: 'moderate', label: 'Moderate' },
              { value: 'weak', label: 'Weak' },
            ]},
            { key: 'local_mills', label: 'Local Mills', type: 'array' },
            { key: 'average_haul_distance', label: 'Avg Haul Distance', type: 'number', unit: 'miles' },
          ],
          suggestedFlags: [
            { flag: 'green', condition: 'Strong timber market' },
            { flag: 'yellow', condition: 'Weak timber market' },
          ],
        },
        {
          code: 'timber_vs_price',
          title: 'Timber value vs asking price',
          description: 'Compare timber value to land price',
          isGroupTask: true,
          dataFields: [
            { key: 'timber_value', label: 'Timber Value', type: 'number', unit: 'dollars' },
            { key: 'asking_price', label: 'Asking Price', type: 'number', unit: 'dollars' },
            { key: 'timber_percent_of_price', label: 'Timber % of Price', type: 'number', unit: 'percent' },
            { key: 'land_value_after_harvest', label: 'Land Value After Harvest', type: 'number', unit: 'dollars' },
          ],
          suggestedFlags: [
            { flag: 'big_green', condition: 'Timber covers >50% of price' },
            { flag: 'green', condition: 'Timber covers 25-50% of price' },
          ],
        },
      ],
    },
    {
      code: 'timber_access',
      name: 'Timber Access',
      items: [
        {
          code: 'logging_access',
          title: 'Logging access assessment',
          description: 'Can timber be harvested efficiently',
          isGroupTask: false,
          dataFields: [
            { key: 'road_access_adequate', label: 'Road Access Adequate', type: 'boolean' },
            { key: 'deck_sites', label: 'Deck Sites Available', type: 'number' },
            { key: 'terrain_difficulty', label: 'Terrain Difficulty', type: 'select', options: [
              { value: 'easy', label: 'Easy - Flat/Rolling' },
              { value: 'moderate', label: 'Moderate' },
              { value: 'difficult', label: 'Difficult - Steep' },
              { value: 'very_difficult', label: 'Very Difficult' },
            ]},
            { key: 'access_improvements_needed', label: 'Access Improvements Needed', type: 'string' },
            { key: 'improvement_cost', label: 'Improvement Cost', type: 'number', unit: 'dollars' },
          ],
          suggestedFlags: [
            { flag: 'red', condition: 'Poor logging access' },
            { flag: 'yellow', condition: 'Access needs improvement' },
          ],
        },
        {
          code: 'bmp_compliance',
          title: 'BMP compliance',
          description: 'Best Management Practices considerations',
          isGroupTask: false,
          dataFields: [
            { key: 'stream_buffers_needed', label: 'Stream Buffers Needed', type: 'boolean' },
            { key: 'wetland_considerations', label: 'Wetland Considerations', type: 'boolean' },
            { key: 'steep_slope_issues', label: 'Steep Slope Issues', type: 'boolean' },
            { key: 'bmp_restrictions', label: 'BMP Restrictions', type: 'string' },
          ],
          suggestedFlags: [
            { flag: 'yellow', condition: 'Significant BMP restrictions' },
          ],
        },
      ],
    },
    {
      code: 'timber_management',
      name: 'Timber Management',
      items: [
        {
          code: 'harvest_history',
          title: 'Harvest history',
          description: 'Previous timber harvests',
          isGroupTask: false,
          dataFields: [
            { key: 'last_harvest_date', label: 'Last Harvest Date', type: 'date' },
            { key: 'harvest_type', label: 'Harvest Type', type: 'string' },
            { key: 'harvest_notes', label: 'Notes', type: 'string' },
          ],
        },
        {
          code: 'management_plan',
          title: 'Forest management plan',
          description: 'Existing management plan review',
          isGroupTask: false,
          dataFields: [
            { key: 'plan_exists', label: 'Plan Exists', type: 'boolean' },
            { key: 'plan_date', label: 'Plan Date', type: 'date' },
            { key: 'plan_preparer', label: 'Plan Preparer', type: 'string' },
            { key: 'plan_recommendations', label: 'Recommendations', type: 'string' },
          ],
        },
        {
          code: 'recommended_practices',
          title: 'Recommended silvicultural practices',
          description: 'Forest management recommendations',
          isGroupTask: false,
          dataFields: [
            { key: 'recommended_actions', label: 'Recommended Actions', type: 'array' },
            { key: 'thinning_needed', label: 'Thinning Needed', type: 'boolean' },
            { key: 'regeneration_needed', label: 'Regeneration Needed', type: 'boolean' },
            { key: 'estimated_management_cost', label: 'Est. Management Cost', type: 'number', unit: 'dollars' },
          ],
        },
        {
          code: 'timber_health',
          title: 'Timber health assessment',
          description: 'Disease, pests, fire risk',
          isGroupTask: false,
          dataFields: [
            { key: 'health_rating', label: 'Health Rating', type: 'select', options: [
              { value: 'excellent', label: 'Excellent' },
              { value: 'good', label: 'Good' },
              { value: 'fair', label: 'Fair' },
              { value: 'poor', label: 'Poor' },
            ]},
            { key: 'disease_present', label: 'Disease Present', type: 'boolean' },
            { key: 'pest_damage', label: 'Pest Damage', type: 'boolean' },
            { key: 'fire_damage', label: 'Fire Damage', type: 'boolean' },
            { key: 'health_notes', label: 'Health Notes', type: 'string' },
          ],
          suggestedFlags: [
            { flag: 'red', condition: 'Significant disease or pest problems' },
            { flag: 'yellow', condition: 'Some health concerns' },
          ],
        },
        {
          code: 'growth_potential',
          title: 'Growth potential',
          description: 'Annual timber growth estimate',
          isGroupTask: false,
          dataFields: [
            { key: 'site_index', label: 'Site Index', type: 'number' },
            { key: 'annual_growth_rate', label: 'Annual Growth Rate', type: 'number', unit: 'percent' },
            { key: 'annual_value_growth', label: 'Annual Value Growth', type: 'number', unit: 'dollars' },
          ],
          suggestedFlags: [
            { flag: 'big_green', condition: 'High site index, good growth' },
          ],
        },
      ],
    },
  ],
};

